// Everything sign-in does once Cognito has accepted the password: load the
// profile, pin the session, warm the caches and go to the right page.
//
// Lifted out of components/signIn.jsx unchanged so the company picker
// (pages/selectCompany.jsx) can finish a sign-in the same way once the person
// has chosen a company. Sign-in for a person with one company still runs this
// straight away, exactly as before.

import { setActiveThemeKey, getBootThemeKey } from '../config/default';
import { THEMES, DEFAULT_THEME_KEY, THEME_RECONCILE_FLAG } from '../config/themes';
import { getSyncArray, setErpTypeInSession } from '../services/common';
import {
  user_information,
  load_synced_data,
  clear_synced_data,
  load_field_config,
  clear_field_config
} from '../redux/actions/action';
import { getInfo, syncData } from '../services/services';
import { userRoles, headings, erpTypes } from '../constants/appConstants';
import { clearSessionPreservingTab } from './tabSession';
import { isFieldEnabled } from './fieldConfig';
import { companyHeader } from '../tenancy/companySession';

const { ADMIN, VENDOR, MANAGER, CLEVEL } = userRoles;
const { QUICKBOOKS_DESKTOP_ERP, VIEWPOINT_SPECTRUM_ERP, VIEWPOINT_VISTA_ERP } = erpTypes;
const { TOKEN } = headings;

/** The headers sign-in uses before the token is in the session. */
export const signInHeaders = (token) => () => ({
  headers: { Authorization: 'Bearer ' + token, ...companyHeader() }
});

/**
 * @returns true when the profile loaded and the user was sent on, false when
 *   the profile read failed (the caller has already been told via `onProfileUnavailable`).
 */
export async function completeSignIn({
  dispatch,
  navigate,
  token,
  accessToken,
  cognitoUsername,
  tokenRole,
  redirectTo,
  onProfileUnavailable
}) {
  const setHeaders = signInHeaders(token);
  const getUserresult = await getInfo(cognitoUsername, setHeaders);
  if (getUserresult?.status !== 200) {
    // The credentials were fine — Cognito already authenticated above.
    // What failed is the profile read, and Tier B now refuses that request
    // outright when it cannot determine the user's role rather than
    // returning one with an empty role. Saying "something went wrong" here
    // sends an account-configuration fault to support as a login outage.
    onProfileUnavailable?.();
    return false;
  }

  sessionStorage.setItem(TOKEN, token);
  sessionStorage.setItem('accessToken', accessToken);
  // The server's answer for the chosen company: for a person with several
  // companies their role can differ between them, and the token holds one.
  const role = getUserresult?.data?.payload?.data?.role || tokenRole;
  const erp = getUserresult?.data?.payload?.data?.user?.erpType;
  sessionStorage.setItem('userRole', role);
  setErpTypeInSession(erp);
  dispatch(user_information(getUserresult?.data?.payload?.data));

  // Fetch the company-wide ERP coding arrays once and cache them in Redux
  // so the HIL/canvas screen reuses them instead of pulling glInformation
  // on every get-document-by-id. Clear any stale arrays first (persisted
  // in localStorage, which most logout paths don't wipe) so a different
  // account can never read the previous user's data before the fetch
  // resolves. Fire-and-forget: it must not block navigation. Skipped for
  // vendors (separate portal, no HIL coding).
  dispatch(clear_synced_data());
  dispatch(clear_field_config());
  const companyId = getUserresult?.data?.payload?.data?.company?._id;
  if (companyId && role !== VENDOR) {
    dispatch(load_synced_data(companyId));
    // The document field configuration replaced eight company permission
    // flags, so it gates coding columns and which ERP lists get synced.
    // Loaded here alongside the coding arrays; initialErpCall awaits its
    // own copy because getSyncArray needs the answer before the first sync.
    dispatch(load_field_config(companyId));
  }

  // Resolve this user's theme from the authoritative profile we just
  // fetched and pin it before navigating. The app booted on the un-themed
  // (dark) login page, so when the user's theme differs we hard-reload
  // into the destination: that lets `Colors` and every module-scope /
  // static-CSS color capture freeze at the RIGHT palette from the first
  // frame, instead of the dashboard painting dark and then reloading.
  // We also set the reconcile flag so themeContext doesn't fire a second
  // (post-render) reload of its own.
  const userTheme = getUserresult?.data?.payload?.data?.user?.theme;
  const resolvedTheme = userTheme && THEMES[userTheme] ? userTheme : DEFAULT_THEME_KEY;
  setActiveThemeKey(resolvedTheme);
  sessionStorage.setItem(THEME_RECONCILE_FLAG, '1');
  const themeReloadNeeded = resolvedTheme !== getBootThemeKey();
  const goTo = (route) => (themeReloadNeeded ? window.location.assign(route) : navigate(route));

  if (redirectTo === 'stripe') {
    goTo('/stripe');
  } else if (role === ADMIN) {
    goTo('/admin');
  } else if (role === VENDOR) {
    goTo('/vendor');
  } else {
    // Kick the ERP sync off BEFORE navigating so its request reaches the
    // server regardless of whether we SPA-navigate or hard-reload for the
    // theme (a hard reload may abort the client-side response handling, as
    // the prior post-login reconcile reload already did).
    if (
      role !== MANAGER &&
      role !== CLEVEL &&
      erp !== QUICKBOOKS_DESKTOP_ERP &&
      erp !== VIEWPOINT_SPECTRUM_ERP &&
      erp !== VIEWPOINT_VISTA_ERP
    ) {
      initialErpCall({
        dispatch,
        navigate,
        userDetails: getUserresult?.data?.payload?.data,
        erpType: erp,
        cognitoUsername,
        setHeaders
      });
    }
    goTo('/home');
  }
  return true;
}

export async function initialErpCall({
  dispatch,
  navigate,
  userDetails,
  erpType,
  cognitoUsername,
  setHeaders
}) {
  if (userDetails?.activeIntegration && userDetails?.activeIntegration !== '') {
    setErpTypeInSession(erpType);
    // Awaited, not read off the store: this runs during login, before the
    // load_field_config dispatched above has necessarily landed, and syncing the
    // wrong lists here leaves the coding dropdowns empty for the whole session.
    const fieldConfig = await dispatch(load_field_config(userDetails?.company?._id));
    let params = {
      data: getSyncArray(erpType, (name) => isFieldEnabled(fieldConfig, name), userDetails?.user)
    };
    const result = await syncData(params);
    if (result?.status === 200) {
      const getUserresult = await getInfo(cognitoUsername, setHeaders);
      if (getUserresult?.status === 200) {
        dispatch(user_information(getUserresult?.data?.payload?.data));
      }
      // ERP data just synced — refresh the cached coding arrays with the
      // updated response.
      if (userDetails?.company?._id) {
        dispatch(load_synced_data(userDetails?.company?._id));
      }
    } else if (result?.response?.status === 401) {
      clearSessionPreservingTab();
      navigate('/');
    }
  }
}
